import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, ApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { fetchProductFromOff } from "../lib/off";
import { recomputeMacros, formatEntryName } from "../lib/barcode";
import { useBarcodeScanner } from "../lib/useBarcodeScanner";
import { ScannerViewfinder } from "./ScannerViewfinder";
import { useEntitlement } from "../lib/useEntitlement";
import { useUnits } from "../lib/useUnits";
import { detailAllowance } from "../lib/detailGate";
import { ProChip } from "./ProLock";
import { Paywall } from "./Paywall";
import { useTopmostEscape } from "../lib/useTopmostEscape";
import { useEdgeSwipe } from "../lib/useEdgeSwipe";
import { hapticSuccess } from "../lib/haptics";
import { MealSlotPicker } from "./MealSlotPicker";
import { FoodSheet } from "./FoodSheet";
import { saveFoodDraft } from "../lib/foodDraft";
import { WeightStepper } from "./WeightStepper";
import type { MealSlot, NormalizedProduct, ServingUnit } from "@shared/types";

interface Props {
  eatenOn: string;
  defaultSlot: MealSlot;
  /** Fired once when the user taps Done (after ≥1 add) — triggers a single Today reload. */
  onDone: () => Promise<void>;
  onClose: () => void;
}

type Phase =
  | { kind: "scanning" }
  | { kind: "looking_up" }
  | { kind: "confirm"; product: NormalizedProduct }
  | { kind: "miss"; code: string };

const LOOKUP_TIMEOUT_MS = 3500;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<never>((_, rej) => setTimeout(() => rej(new Error("timeout")), ms)),
  ]);
}

export function ContinuousScanSheet({ eatenOn, defaultSlot, onDone, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const { premium } = useEntitlement();
  const units = useUnits();
  const [showPaywall, setShowPaywall] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: "scanning" });
  const [count, setCount] = useState(0);
  const [slot, setSlot] = useState<MealSlot>(defaultSlot); // sticky across items this session
  const [amount, setAmount] = useState(100);
  const [unit, setUnit] = useState<ServingUnit>("g");
  const [manualOpen, setManualOpen] = useState(false);
  const busyRef = useRef(false);
  const [adding, setAdding] = useState(false);
  const [keep, setKeep] = useState(false);
  // Minted whenever a new confirmation opens; addCurrent sends it as the
  // idempotency key for that one product.
  const confirmIdRef = useRef<string>(crypto.randomUUID());
  const rootRef = useRef<HTMLDivElement>(null);

  useTopmostEscape(onClose);
  // Edge swipe leaves the scanner like the Done button (reloads if ≥1 added).
  useEdgeSwipe(rootRef, { onCommit: () => { void done(); } });

  const { videoRef, state, pause, resume, retry, torchSupported, torchOn, toggleTorch } = useBarcodeScanner({
    onDecode: (code) => { void onDecode(code); },
  });

  async function onDecode(code: string) {
    if (busyRef.current) return;
    busyRef.current = true;
    pause();
    setPhase({ kind: "looking_up" });
    api.barcodeRecordRecent(code).catch(() => { /* ignore */ });
    try {
      const cached = await withTimeout(api.barcodeLookup(code), LOOKUP_TIMEOUT_MS);
      if (cached.status === "hit")  return toConfirm(cached.product);
      if (cached.status === "miss") return toMiss(code);
      const product = await fetchProductFromOff(code);
      api.barcodeCachePut(code, product).catch(() => { /* best-effort */ });
      return product ? toConfirm(product) : toMiss(code);
    } catch {
      return toMiss(code);
    } finally {
      busyRef.current = false;
    }
  }

  function toConfirm(product: NormalizedProduct) {
    setAmount(product.serving_size_g && product.serving_size_g > 0 ? product.serving_size_g : 100);
    setUnit(product.unit ?? "g");
    confirmIdRef.current = crypto.randomUUID();
    setKeep(false);
    setPhase({ kind: "confirm", product });
  }
  function toMiss(code: string) {
    setPhase({ kind: "miss", code });
  }

  /**
   * Return to scanning. We MUST seed the debounce with the barcode we just
   * handled (confirm → product.code, miss → code), because that barcode is very
   * likely still in the camera frame; without seeding, the decoder would
   * immediately re-fire it and produce a duplicate confirm card or an unwanted
   * miss loop (the Skip and miss→manual paths have no confirm gate to absorb it).
   * Capture the code from the current phase BEFORE resetting it to "scanning".
   */
  function backToScanning() {
    const justHandled =
      phase.kind === "confirm" ? phase.product.code :
      phase.kind === "miss"    ? phase.code :
      undefined;
    setPhase({ kind: "scanning" });
    resume(justHandled);
  }

  async function addCurrent() {
    if (phase.kind !== "confirm" || adding) return; // double-tap can't double-log
    setAdding(true);
    const amt = Math.min(2000, Math.max(1, Math.round(amount)));
    const macros = recomputeMacros(phase.product.per_100g, amt);
    try {
      await api.addFood({
        // One key per confirmation: a retry of this exact add converges on the
        // server's client_id dedupe instead of writing another row.
        client_id: confirmIdRef.current,
        eaten_on: eatenOn,
        source: "custom",
        plan_meal_slot: slot,
        name: formatEntryName(phase.product),
        calories: macros.calories,
        // Detail amounts are Pro — a free scan logs the bare entry.
        ...detailAllowance(premium, {
          protein_g: macros.protein_g,
          carbs_g: macros.carbs_g,
          fat_g: macros.fat_g,
          sugar_g: macros.sugar_g,
          micros: macros.micros,
        }),
      });
    } catch (e) {
      // The Add button must never wedge: surface the failure, stay on the card.
      if (e instanceof ApiError && e.message === "premium_required") setShowPaywall(true);
      else toast.error(t("toast.failed"));
      setAdding(false);
      return;
    }
    api.barcodeRecordRecent(phase.product.code).catch(() => { /* ignore */ });
    if (keep && premium) {
      api.createSavedMeal(formatEntryName(phase.product), null, [{
        name: formatEntryName(phase.product), kcal: macros.calories,
        protein_g: macros.protein_g ?? null, carbs_g: macros.carbs_g ?? null,
        fat_g: macros.fat_g ?? null, sugar_g: macros.sugar_g ?? null,
        micros_json: macros.micros ? JSON.stringify(macros.micros) : null,
        grams: amt, unit,
      }]).then(() => toast.success(t("saved.saved_toast")))
        .catch(() => { /* best-effort — the log itself already landed */ });
    }
    hapticSuccess();
    setCount((n) => n + 1);
    setAdding(false);
    backToScanning();
  }

  async function done() {
    if (count > 0) await onDone();
    onClose();
  }

  const missCode = phase.kind === "miss" ? phase.code : null;

  // The manual-entry FoodSheet and the Paywall portal to <body> at z-50 while
  // this scanner root sits at z-[60] — without hiding the scanner they open
  // BEHIND the camera view, i.e. "tapping the button does nothing" (user bug
  // report 2026-08-15). visibility:hidden also drops hit-testing, and the
  // paused camera stream stays alive for an instant resume.
  const childSheetOpen = (manualOpen && missCode !== null) || showPaywall;
  return createPortal(
    <div ref={rootRef} data-theme="dark" className={`fixed inset-0 z-[60] bg-canvas text-fg flex flex-col ${childSheetOpen ? "invisible" : ""}`}>
      <div className="flex justify-between items-center px-4 pt-safe pt-4">
        <span className="text-sm text-fg-secondary tabular-nums">{t("scan.n_added", { n: count })}</span>
        <button onClick={done} className="text-fg text-sm font-medium py-2 px-1">{t("scan.done")}</button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {/* NOTE: no `autoPlay` — the hook calls play() explicitly (iOS AbortError trap). */}
        <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" />

        {phase.kind === "scanning" && state.kind === "scanning" && (
          <ScannerViewfinder
            hint={t("scan.aim")}
            torchSupported={torchSupported}
            torchOn={torchOn}
            onToggleTorch={() => void toggleTorch()}
          />
        )}
        {state.kind === "idle" && (
          <div className="absolute inset-0 flex items-center justify-center text-fg-secondary text-sm">{t("barcode.starting_camera")}</div>
        )}
        {phase.kind === "looking_up" && (
          <div className="absolute inset-0 flex items-center justify-center text-fg-secondary text-sm bg-black/40">{t("barcode.looking_up")}</div>
        )}

        {phase.kind === "confirm" && (
          <div className="absolute inset-x-0 bottom-0 bg-elevated/95 backdrop-blur rounded-t-3xl px-5 pt-4 pb-safe pb-5 space-y-3">
            <div className="text-sm font-medium truncate">{formatEntryName(phase.product)}</div>
            {premium ? (
              <div className="text-sm text-fg-muted tabular-nums">
                {units.energyStr(phase.product.per_100g.kcal)}/100 {unit} · P {phase.product.per_100g.protein_g ?? "-"}g
                · C {phase.product.per_100g.carbs_g ?? "-"}g · F {phase.product.per_100g.fat_g ?? "-"}g
              </div>
            ) : (
              // The moved gate: calories stay, the detail values are Pro.
              <div className="flex items-center gap-2 text-sm text-fg-muted tabular-nums">
                <span>{units.energyStr(phase.product.per_100g.kcal)}/100 {unit}</span>
                <ProChip onUpgrade={() => setShowPaywall(true)} />
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-fg-muted">{t("scan.amount")}</span>
              <WeightStepper
                value={amount}
                unit={unit}
                onChange={(a, u) => { setAmount(a); setUnit(u); }}
                max={2000}
                aria-label={t("scan.amount")}
              />
              <span className="text-sm text-fg-muted tabular-nums">
                {units.energyStr(recomputeMacros(phase.product.per_100g, Math.max(1, Math.round(amount))).calories)}
              </span>
            </div>
            <div>
              <div className="text-sm text-fg-muted mb-1.5">{t("scan.meal")}</div>
              <MealSlotPicker value={slot} onChange={setSlot} />
            </div>
            {/* Keep this product as a saved meal too (owner: bookmarking is
                for single foods as much as whole meals). Saved meals are
                Premium, so the control only exists there. */}
            {premium && (
            <label className="flex items-center gap-2 text-sm pt-1">
              <input
                type="checkbox"
                checked={keep}
                onChange={(e) => setKeep(e.target.checked)}
                className="h-5 w-5 accent-white"
              />
              <span>{t("saved.save_as_meal")}</span>
            </label>
            )}
            <div className="flex gap-2 pt-1">
              <button onClick={addCurrent} disabled={adding} className="flex-1 py-3 bg-brand text-on-brand press rounded-full font-medium">{t("scan.add")}</button>
              <button onClick={backToScanning} className="px-5 py-3 bg-surface-strong rounded-full">{t("scan.skip")}</button>
            </div>
          </div>
        )}

        {phase.kind === "miss" && (
          <div className="absolute inset-x-0 bottom-0 bg-elevated/95 backdrop-blur rounded-t-3xl px-5 pt-4 pb-safe pb-5 space-y-3">
            <p className="text-sm text-warn/90">{t("scan.miss")}</p>
            <div className="flex gap-2">
              <button onClick={() => setManualOpen(true)} className="flex-1 py-3 bg-brand text-on-brand press rounded-full font-medium">{t("scan.enter_manually")}</button>
              <button onClick={backToScanning} className="px-5 py-3 bg-surface-strong rounded-full">{t("scan.skip")}</button>
            </div>
          </div>
        )}

        {state.kind === "denied" && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-4 px-6 text-center">
            <p className="text-sm">{t("barcode.permission_denied")}</p>
            <div className="flex gap-2">
              <button onClick={retry} className="bg-surface-strong rounded-2xl px-4 py-2 text-sm">{t("barcode.try_again")}</button>
              <button onClick={onClose} className="bg-surface-strong rounded-2xl px-4 py-2 text-sm">{t("barcode.cancel")}</button>
            </div>
          </div>
        )}
        {state.kind === "no_camera" && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-4 text-sm">
            <p>{t("barcode.no_camera")}</p>
            <button onClick={onClose} className="bg-surface-strong rounded-2xl px-4 py-2">{t("barcode.cancel")}</button>
          </div>
        )}
        {state.kind === "error" && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-4 text-sm px-6 text-center">
            <p>{state.message}</p>
            <button onClick={retry} className="bg-surface-strong rounded-2xl px-4 py-2">{t("barcode.try_again")}</button>
          </div>
        )}
      </div>

      {/* Miss → manual entry. Reuses FoodSheet so the M2.8 learning-cache write-back
          fires on save (scannedCode set via initial). onSaved counts + resumes. */}
      {manualOpen && missCode && (
        <FoodSheet
          open
          mode="add"
          initial={{}}
          context={{ eaten_on: eatenOn, source: "custom", plan_meal_slot: slot }}
          scannedCode={missCode}
          onSaved={async (draft) => { await saveFoodDraft(draft); setCount((n) => n + 1); }}
          onClose={() => { setManualOpen(false); backToScanning(); }}
        />
      )}
      {showPaywall && <Paywall trigger="feature" onClose={() => setShowPaywall(false)} />}
    </div>,
    document.body,
  );
}
